import GoogleAnalytics from '@disco/api/ga'
import IntercomClient from '@disco/api/intercom'
import type { Location } from '@disco/api/mixpanel'
import MixpanelClient from '@disco/api/mixpanel'
import { PHONE } from '@disco/constants/DeviceTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getUser } from '@disco/state-management/selectors/user'
import { getTopCookieDomain } from '@disco/utils/cookie-consent'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import type { User } from 'types/user'
import useScript from '@disco/utils/use-script'
export const PUBLIC_LOCATIONS: Location[] = [
  'Libraries',
  'Share',
  'Upload',
  'Website',
  'Signup',
]
export type ScriptType = 'GA' | 'Mixpanel' | 'Intercom'
type Props = {
  scriptsAllowed: boolean
  regionDetected: boolean
  location: Location
  // TODO: move specific third-party script initialization params
  // to this config object
  scriptParams?: Partial<Record<ScriptType, Record<string, any>>>
}
const FIRST_PARTY_COOKIES = [
  'csrftoken',
  'sessionid',
  'minidiscoadmin_frontend_version',
  'disco_businesses',
  'tracking_consent_optin',
]
const THIRD_PARTY_ESSENTIAL_COOKIES = ['intercom']
const webAppScripts: ScriptType[] = ['GA', 'Mixpanel', 'Intercom']
const publicPageScripts: ScriptType[] = ['GA', 'Mixpanel']
const websiteScripts: ScriptType[] = ['GA', 'Mixpanel', 'Intercom']
const scriptsMap: Partial<Record<Location, ScriptType[]>> = {
  Upload: publicPageScripts,
  Share: publicPageScripts,
  Libraries: publicPageScripts,
  Website: websiteScripts,
  Login: webAppScripts,
  Explorer: webAppScripts,
  Signup: publicPageScripts,
}
export const ThirdPartyScripts = ({
  scriptsAllowed,
  regionDetected,
  location,
  scriptParams = {},
}: Props): null => {
  const scripts = scriptsMap[location]
  const [mixpanelInitialized, setMixpanelInitialized] = useState(false)
  const [intercomInitialized, setIntercomInitialized] = useState(false)
  const [gaInitialized, setGAInitialized] = useState(false)
  const user: User = useSelector(getUser)
  const mobileDeviceType = useSelector(state => state.app?.mobileDeviceType)
  const isSupportMenuFeatureEnabled = useFeatureEnabled('SUPPORT_MENU') // TODO: remove temporary rollout flag

  const GA_DEFAULT_ID = 'G-FKL4G9VWST'
  const gaScriptId =
    scriptParams?.GA?.IDS && scriptParams?.GA?.IDS.length > 0
      ? scriptParams?.GA?.IDS[0]
      : GA_DEFAULT_ID
  // GA
  const [gaLoaded] = useScript(
    `//www.googletagmanager.com/gtag/js?id=${gaScriptId}`,
    null,
    !scripts?.includes('GA') || !scriptsAllowed,
    true,
  )
  useEffect(
    function initGA() {
      const initGoogleAnalytics = (
        ...args: Parameters<typeof GoogleAnalytics.init>
      ) => {
        GoogleAnalytics.init(...args)
        setGAInitialized(true)
      }

      if (gaLoaded && !gaInitialized && scriptParams) {
        if (location === 'Libraries' && scriptParams?.GA?.display_name) {
          initGoogleAnalytics(
            null,
            scriptParams?.GA?.display_name,
            scriptParams?.GA?.IDS,
          )
        } else if (user.loggedIn) {
          initGoogleAnalytics(
            user.id,
            user.business.name,
            scriptParams?.GA?.IDS,
          )
        } else if (
          PUBLIC_LOCATIONS.includes(location) &&
          window.business_data
        ) {
          initGoogleAnalytics(
            null,
            window.business_data.name,
            scriptParams?.GA?.IDS,
          )
        } else if (location === 'Website') {
          initGoogleAnalytics(null, '', scriptParams?.GA?.IDS)
        }

        // send page view after GA was loaded
        GoogleAnalytics.sendPageview()
      }
    },
    [
      gaLoaded,
      gaInitialized,
      scriptsAllowed,
      scriptParams,
      user,
      scripts,
      location,
    ],
  )
  useEffect(
    function initMixpanel() {
      if (
        scripts?.includes('Mixpanel') &&
        scriptsAllowed &&
        !mixpanelInitialized
      ) {
        // TODO: add async loading for Mixpanel package
        if (user.loggedIn) {
          MixpanelClient.setup(user, user.business, {
            location,
          })
          setMixpanelInitialized(true)
        } else if (
          PUBLIC_LOCATIONS.includes(location) &&
          (window.business_data || user.business)
        ) {
          // CL's catalog pages never set window.business_data (they fetch it
          // via loadUser -> getCurrentBusinessPublic into Redux instead), so
          // fall back to user.business for anonymous visitors there.
          MixpanelClient.setup(
            undefined,
            window.business_data || user.business,
            {
              location,
            },
          )
          setMixpanelInitialized(true)
        } else {
          MixpanelClient.setup(undefined, undefined, {
            location,
          })
          setMixpanelInitialized(true)
        }
      }
    },
    [scripts, scriptsAllowed, mixpanelInitialized, user, location],
  )
  useEffect(() => {
    if (mixpanelInitialized) {
      MixpanelClient.updateLocation(location)
    }
  }, [location, mixpanelInitialized])
  // Intercom
  const [intercomLoaded] = useScript(
    'https://widget.intercom.io/widget/idkf9hdp',
    null,
    !scripts?.includes('Intercom'),
  )
  useEffect(
    function initIntercom() {
      try {
        if (
          scripts?.includes('Intercom') &&
          mobileDeviceType !== PHONE &&
          (user.loggedIn || location === 'Website') &&
          intercomLoaded
        ) {
          if (user.loggedIn)
            IntercomClient.setup(user, user.business, {
              isSupportMenuFeatureEnabled,
            })
          if (location === 'Website') IntercomClient.setup(null, null, {})
          setIntercomInitialized(true)
        }
      } catch (e) {
        console.error('Failed to initialize Intercom', e)
      }
    },
    [scripts, mobileDeviceType, user, location, intercomLoaded],
  )
  useEffect(
    function syncUser() {
      // TODO: add Intercom  sync if needed (currently it's not being used on public pages and exists in-app only when user is already logged in)
      // TODO: add GA user sync if needed (currently GA is being used only for basic stats, so doesn't matter)
      if (user.loggedIn && mixpanelInitialized) {
        MixpanelClient.syncUserInfo(user, user.business, {
          location,
        })
      }
    },
    [user, location, mixpanelInitialized],
  )
  // clean up cookies if consent is not given, but cookies are present
  useEffect(
    function removeCookies() {
      if (regionDetected && !scriptsAllowed) {
        const cookiesToRemove = Object.keys(Cookies.get()).filter(
          c =>
            !(
              FIRST_PARTY_COOKIES.includes(c) || // wildcard match
              THIRD_PARTY_ESSENTIAL_COOKIES.find(cookie => c.includes(cookie))
            ),
        )
        const domain = getTopCookieDomain()
        cookiesToRemove.forEach(c => {
          // remove domain/subdomain cookies
          Cookies.remove(c, {
            path: '/',
            domain,
          })
          // remove domain-only cookies
          Cookies.remove(c, {
            path: '/',
            domain: domain.replace(/^\./, ''),
          })
        })
      }
    },
    [regionDetected, scriptsAllowed],
  )
  return null
}
